import { cookieStore, getToken, type AuthSessionLike } from '@bettercomp/ams-spa-auth';

/**
 * Module bridge between the React auth provider and non-React callers (fetch
 * seams, logout helper, tenant switch). AmsAuthProvider owns the session
 * lifecycle and keeps this reference current; everything outside the component
 * tree reads it here instead of reaching into React context.
 */
let amsSession: AuthSessionLike | undefined;

export const setAmsSession = (session: AuthSessionLike | undefined): void => {
  amsSession = session;
};

export const getAmsSession = (): AuthSessionLike | undefined => amsSession;

/**
 * Whether this browser holds an AMS session artifact — the amsAuth refresh
 * cookie. Mode dispatch is artifact-driven: its presence selects the AMS auth
 * stack on load, its absence the legacy one; no feature-flag read is involved.
 */
export const hasAmsSessionArtifact = (): boolean => typeof document !== 'undefined' && cookieStore.get() !== null;

/**
 * Whether this browser is on the AMS auth stack in any phase: a live in-memory
 * access token; a constructed session — which outlives its own death, because
 * a sessionExpired/terminal renewal clears the token store and the amsAuth
 * cookie synchronously inside renew(), before the surfaced 401 reaches any
 * caller; or the amsAuth artifact alone (page boot before the session is
 * built, or a stale legacy tab after another tab's adoption).
 *
 * The legacy 401 handlers gate their logout() on the negation of this: a bare
 * getToken() read taken after fetchWithRenewal would misclassify a just-died
 * AMS request as legacy and fire the legacy Cognito logout against the auth
 * gate's own expired-session redirect. Delete this together with those legacy
 * 401 → logout() branches when the legacy Cognito flow is removed.
 */
export const isAmsAuthActive = (): boolean =>
  Boolean(getToken()) || amsSession !== undefined || hasAmsSessionArtifact();
